<template>
  <div id="page-consultant-list">

    <!-- Top Header Area -->
    <div class="page-header">
      <div class="header-content">
        <h2 class="page-title">Consultants & Offers</h2>
        <p class="page-subtitle">Connect with domain experts and book specific service packages tailored to your startup's needs.</p>
      </div>
    </div>

    <!-- Toolbar: Search & Filter -->
    <div class="toolbar">
      <div class="search-container">
        <feather-icon icon="SearchIcon" svgClasses="w-5 h-5 text-muted mr-2" />
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="Search by name, AI Strategy, or keywords..."
        />
        <!-- Clear button appears only when typing -->
        <feather-icon 
          v-if="searchQuery" 
          icon="XIcon" 
          svgClasses="w-4 h-4 text-muted cursor-pointer ml-2 hover:text-dark transition-colors" 
          @click="searchQuery = ''" 
        />
      </div>
    </div>

    <!-- Sleek Filter Panel -->
    <transition name="fade-slide">
      <div v-if="showFilters" class="filter-panel">
        <div class="filter-section-wrapper">
          <div class="filter-group category-group">
            <h5 class="filter-heading">CATEGORIES</h5>
            <div class="pill-group">
              <button
                v-for="(cat, index) in categoryOptions"
                :key="index"
                @click="selectedCategory = cat.name"
                class="filter-pill"
                :class="{ 'is-active': selectedCategory === cat.name }"
              >
                {{ cat.name }}
              </button>
            </div>
          </div>
        </div>
        <div class="filter-actions" v-if="hasActiveFilters">
          <button class="clear-filters-btn" @click="resetFilters">
            <feather-icon icon="XIcon" svgClasses="w-4 h-4" /> Clear all
          </button>
        </div>
      </div>
    </transition>

    <div class="layout-grid">
      <!-- RIGHT SIDE: OFFERS GRID -->
      <section class="results">
        
        <!-- IMPROVED EMPTY STATE -->
        <div v-if="filteredOffers.length === 0" class="empty-state-card">
          <div class="empty-state-content">
            <div class="empty-icon-wrap">
              <feather-icon v-if="!searchQuery" icon="InboxIcon" svgClasses="w-24 h-24" />
              <feather-icon v-else icon="SearchIcon" svgClasses="w-8 h-8" />
            </div>
            
            <h4 class="empty-title">{{ searchQuery ? 'No offers found' : 'No Consultants Yet' }}</h4>
            
            <p class="empty-text" v-if="!searchQuery">
              It looks like there are no consulting offers available at the moment.<br>
            </p>
            <p class="empty-text" v-else>
              We couldn't find anything matching "<strong>{{ searchQuery }}</strong>".
            </p>
            
            <button class="btn-clear-search" v-if="searchQuery" @click="searchQuery = ''">
              Clear search
            </button>
          </div>
        </div>

        <!-- CONSULTING OFFERS GRID -->
        <div v-else class="consulting-offers-grid">

          <article
              v-for="offer in filteredOffers"
              :key="offer._id"
              :class="['lovable-offer-card', { 'is-hidden-offer': offer.isHiddenForCurrentOrg }]"
              @click="viewProfile(offer)"
          >
              <!-- COLORED HEADER (Replaces Image) -->
              <div class="loc-header" :style="{ backgroundColor: offer.profileColor || '#2563eb' }">
                <div class="loc-header-top">
                    <div class="loc-avatar">
                      <img v-if="offer.consultant_full_data.profile_image_url" :src="getProfileImageUrl(offer.consultant_full_data.profile_image_url)" alt="Consultant" />
                      <div v-else class="loc-avatar-placeholder">{{ offer.consultant_full_data.name.charAt(0) }}</div>
                    </div>
                    
                    <!-- 👉 VISIBILITY TOGGLE (Only Admins see this) -->
                    <div style="display: flex; gap: 8px; align-items: center;">
                        <span v-if="offer.bestFitFor" class="loc-badge">{{ offer.bestFitFor }}</span>
                        
                        <button 
                            v-if="isActuallyAdminView" 
                            @click.stop="toggleVisibility(offer)" 
                            class="visibility-toggle-btn" 
                            :title="offer.isHiddenForCurrentOrg ? 'Hidden from users. Click to show.' : 'Visible to users. Click to hide.'"
                        >
                            <feather-icon :icon="offer.isHiddenForCurrentOrg ? 'EyeOffIcon' : 'EyeIcon'" svgClasses="w-4 h-4" />
                        </button>
                    </div>
                </div>
                
                <h3 class="loc-title">{{ offer.title }}</h3>
                <div class="loc-consultant-name">{{ offer.consultant_full_data.name }} &middot; {{ offer.consultant_full_data.company_name || 'Consultant' }}</div>
              </div>
              
              <!-- CARD BODY -->
              <div class="loc-body">
                <p class="loc-promise">{{ offer.valuePromise || offer.shortDescription || 'View details for more information.' }}</p>
                
                <div class="loc-tags">
                    <span v-if="offer.firstStepType && offer.firstStepType !== 'none'" class="loc-tag">
                    <feather-icon icon="ZapIcon" svgClasses="w-3 h-3 mr-1" /> Low-risk first step
                    </span>
                    <span v-if="offer.valueExchangeAccepted" class="loc-tag">
                    <feather-icon icon="RefreshCcwIcon" svgClasses="w-3 h-3 mr-1" /> Open to value exchange
                    </span>
                    <span v-if="offer.priceFlexibility && offer.priceFlexibility !== 'Fixed price'" class="loc-tag">
                    <feather-icon icon="TagIcon" svgClasses="w-3 h-3 mr-1" /> {{ offer.priceFlexibility }}
                    </span>
                </div>
                
                <div class="loc-footer">
                    <div class="loc-price">
                    <span class="loc-price-val">€{{ offer.price || 'Custom' }}</span>
                    <span class="loc-price-meta" v-if="offer.deliveryWindow"><feather-icon icon="ClockIcon" svgClasses="w-3 h-3" /> {{ offer.deliveryWindow }}</span>
                    </div>
                    <div class="loc-slots" v-if="offer.slotsOpen">
                    <feather-icon icon="CheckCircleIcon" svgClasses="w-3 h-3 text-warning mr-1"/> {{ offer.slotsOpen }} slot(s)
                    </div>
                </div>
              </div>
          </article>

        </div>
      </section>
    </div>

    <!-- BULLETPROOF MODAL -->
    <vs-popup class="profile-modal-premium" title=" " :active.sync="showProfileModal">
      <div v-if="selectedConsultant" class="modal-content-wrapper">
        
        <!-- Pinned Close Button -->
        <button class="modal-close-btn" @click="showProfileModal = false">
          <feather-icon icon="XIcon" svgClasses="w-5 h-5" />
        </button>

        <!-- Invisible Scroll Area -->
        <div class="modal-scroll-area">
          
          <!-- ⭐ LINKEDIN STYLE HEADER ⭐ -->
          <div class="linkedin-header">
  
          <!-- SOLID COLOR BANNER WITH TITLE -->
          <div 
          class="linkedin-cover" 
          :style="{ backgroundColor: selectedOffer ? selectedOffer.profileColor : '#2563eb' }"
        >
          <!-- TITLE IN BANNER -->
          <div class="cover-title-wrapper" v-if="selectedOffer">
            <h2 class="cover-title">{{ selectedOffer.title }}</h2>
            <p class="cover-subtitle" v-if="selectedOffer.bestFitFor">
              Best fit for: {{ selectedOffer.bestFitFor }}
            </p>
          </div>
        </div>

          <div class="linkedin-profile-section">
            <div class="linkedin-avatar-box">
              <img v-if="selectedConsultant.profile_image_url" :src="getProfileImageUrl(selectedConsultant.profile_image_url)" alt="Profile" class="linkedin-avatar-img" />
              <div v-else class="linkedin-avatar-default">
                  <feather-icon icon="UserIcon" svgClasses="w-10 h-10" />
              </div>
            </div>
          </div>

          <div class="linkedin-text-info">
            <h1 class="linkedin-name">{{ selectedConsultant.name }}</h1>
            <p class="linkedin-headline">{{ selectedConsultant.company_name || 'Independent Consultant' }} &bull; AI Specialist</p>
            
            <div class="linkedin-contact-links">
              <a :href="'mailto:' + selectedConsultant.email" class="lnk-link">
                <feather-icon icon="MailIcon" svgClasses="w-3.5 h-3.5" /> {{ selectedConsultant.email }}
              </a>
              <a v-if="selectedConsultant.contact_no" :href="'tel:' + selectedConsultant.contact_no" class="lnk-link">
                <feather-icon icon="PhoneIcon" svgClasses="w-3.5 h-3.5" /> {{ selectedConsultant.contact_no }}
              </a>
            </div>
          </div>
        </div>

          <!-- MODAL BODY -->
          <div class="modal-body">
            
            <div class="modal-section" v-if="selectedConsultant.offers && selectedConsultant.offers.length > 0">
              <h5 class="section-title">Consulting Packages</h5>
              
              <div class="premium-offers-list">
                <div v-for="(offer, idx) in [selectedOffer]" :key="idx" class="detailed-offer-card" v-if="selectedOffer">
                    
                    <!-- MAIN CONTRACT AREA -->
                    <div class="doc-main">
                    <!-- Top Row: Promise & Price -->
                    <div class="doc-top-row">
                        <h3 class="doc-promise">{{ offer.valuePromise || offer.title }}</h3>
                        <div class="doc-price-box">
                        <div class="doc-price-val">€{{ offer.price || 'Custom' }}</div>
                        <div class="doc-price-meta" v-if="offer.deliveryWindow"><feather-icon icon="ClockIcon" svgClasses="w-3 h-3 mr-1"/> {{ offer.deliveryWindow }}</div>
                        </div>
                    </div>

                    <hr class="doc-divider" />

                    <!-- Client Outcome -->
                    <div class="doc-section" v-if="offer.clientOutcome">
                        <label class="doc-label">Client outcome</label>
                        <p class="doc-text">{{ offer.clientOutcome }}</p>
                    </div>

                    <hr class="doc-divider" v-if="offer.clientOutcome" />

                    <!-- Deliverables -->
                    <div class="doc-section" v-if="offer.deliverables && offer.deliverables.length">
                        <label class="doc-label">What the client receives</label>
                        <ul class="doc-check-list">
                        <li v-for="(item, i) in offer.deliverables" :key="i">
                            <feather-icon icon="CheckIcon" svgClasses="text-warning w-4 h-4 mr-2" /> <span>{{ item }}</span>
                        </li>
                        </ul>
                    </div>

                    <hr class="doc-divider" v-if="offer.deliverables && offer.deliverables.length" />

                    <!-- Grid: Format & Basis -->
                    <div class="doc-grid-2">
                        <div class="doc-section" v-if="offer.format">
                        <label class="doc-label">Engagement</label>
                        <div class="doc-text-bold">{{ offer.format }}</div>
                        </div>
                        <div class="doc-section" v-if="offer.paymentBasis">
                        <label class="doc-label">Payment basis</label>
                        <div class="doc-text-bold">{{ offer.paymentBasis }}</div>
                        </div>
                    </div>

                    <!-- CTA -->
                    <button class="doc-cta-btn" @click="askBeforeBook(offer)">
                        <feather-icon icon="FileTextIcon" svgClasses="w-4 h-4 mr-2" /> Request this offer
                    </button>
                    <p class="doc-cta-hint">Requesting creates an evidence contract. Nothing is charged until both parties sign.</p>
                    </div>

                    <!-- "BEFORE YOU COMMIT" AREA -->
                    <div class="doc-commit-box" v-if="offer.firstStepType || offer.responseTime || offer.ifNotUseful || offer.whoNotFor || offer.notIncluded || offer.clientProvides">
                    <h4 class="doc-commit-title">Before you commit</h4>
                    
                    <!-- Low Risk Step -->
                    <div class="doc-commit-item" v-if="offer.firstStepType && offer.firstStepType !== 'none'">
                        <feather-icon icon="ZapIcon" svgClasses="w-5 h-5 text-warning flex-shrink-0 mt-1" />
                        <div class="doc-commit-content">
                        <strong>Low-risk first step available</strong>
                        <p>{{ offer.firstStepType }} <span v-if="offer.firstStepTakeaway">— {{ offer.firstStepTakeaway }}</span></p>
                        </div>
                    </div>

                    <!-- Guarantee -->
                    <div class="doc-commit-item" v-if="offer.ifNotUseful">
                        <feather-icon icon="ShieldIcon" svgClasses="w-5 h-5 text-success flex-shrink-0 mt-1" />
                        <div class="doc-commit-content">
                        <strong>Guarantee</strong>
                        <p>{{ offer.ifNotUseful }}</p>
                        </div>
                    </div>

                    <!-- Exclusions -->
                    <div class="doc-commit-item" v-if="offer.whoNotFor || offer.notIncluded">
                        <feather-icon icon="MinusCircleIcon" svgClasses="w-5 h-5 text-danger flex-shrink-0 mt-1" />
                        <div class="doc-commit-content">
                        <strong>Scope Exclusions</strong>
                        <p v-if="offer.whoNotFor"><strong>Not for:</strong> {{ offer.whoNotFor }}</p>
                        <p v-if="offer.notIncluded"><strong>Excludes:</strong> {{ offer.notIncluded }}</p>
                        </div>
                    </div>

                    <!-- Client Responsibilities -->
                    <div class="doc-commit-item" v-if="offer.clientProvides">
                        <feather-icon icon="ArchiveIcon" svgClasses="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <div class="doc-commit-content">
                        <strong>What you'll need to provide</strong>
                        <p>{{ offer.clientProvides }}</p>
                        </div>
                    </div>

                    <p class="doc-reply-time" v-if="offer.responseTime">Replies {{ offer.responseTime }}.</p>
                    </div>

                </div>
                </div>
            </div>

          </div>
        </div>
      </div>
    </vs-popup>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    isAdminView: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchQuery: "",
      sortBy: "relevance",
      selectedCategory: "All Categories",
      showFilters: false,
      showProfileModal: false,
      selectedOffer: null,
      selectedConsultant: null,
      
      userOrgsList: [],       
      consultantsByOrg: {}, 
      isLoadingData: true,

      filters: { availableThisWeek: false, availableNextWeek: false },
      categoryOptions: [
        { name: 'All Categories' }, { name: 'AI & Machine Learning' }, { name: 'Cybersecurity' },
        { name: 'Cloud Architecture' }, { name: 'UX/UI & Product Design' }, { name: 'Pricing Strategy' },
        { name: 'Enterprise Sales' }, { name: 'Fundraising' }
      ]
    };
  },
  computed: {
    isActuallyAdminView() {
      if (this.isAdminView) return true;

      const path = (this.$route && this.$route.path) ? this.$route.path.toLowerCase() : '';
      if (path.includes('/admin/')) return true;

      let userType = '';
      if (this.$store && this.$store.state.AppActiveUser) {
          userType = this.$store.state.AppActiveUser.userType || this.$store.state.AppActiveUser.user_type || '';
      }
      if (!userType) {
          try {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            userType = userInfo.userType || userInfo.user_type || '';
          } catch(e) {}
      }
      
      const typeStr = String(userType).toLowerCase();
      return typeStr === 'coach' || typeStr === 'admin';
    },
    activeFilterCount() {
      let count = 0;
      if (this.selectedCategory !== 'All Categories') count++;
      if (this.filters.availableThisWeek) count++;
      if (this.filters.availableNextWeek) count++;
      return count;
    },
    hasActiveFilters() { 
      return this.activeFilterCount > 0 || this.searchQuery !== ""; 
    },

    currentMongoOrgId() {
      const activeOrgId = this.$store && this.$store.state.activeCoachOrgId;

      if (activeOrgId) {
        return activeOrgId;
      }

      let name = null;
      if (window.$cookies) {
        let cookieName = window.$cookies.get('companyName') || window.$cookies.get('organization_name');
        if (cookieName) name = decodeURIComponent(cookieName.replace(/\+/g, ' '));
      }
      if (!name) {
         name = localStorage.getItem('companyName') || localStorage.getItem('organization_name');
      }

      if (name && this.userOrgsList.length) {
        const cleanName = String(name).toLowerCase().trim();
        const found = this.userOrgsList.find(o => {
          const iterName = String(o.org_name || o.organization_name || '').toLowerCase().trim();
          return iterName === cleanName;
        });
        if (found) return found.org_id;
      }

      return null;
    },

    allOffers() {
      let offersList = [];
      const orgId = this.currentMongoOrgId;
      
      if (!orgId || !this.consultantsByOrg[orgId]) return offersList;

      const orgConsultants = this.consultantsByOrg[orgId];

      orgConsultants.forEach(consultant => {
        if (consultant.offers && Array.isArray(consultant.offers)) {
          consultant.offers.forEach(offer => {
            
            let isValidForOrg = true;
            if (offer.organization_id) {
               try {
                   let parsedOrgs = offer.organization_id;
                   if (typeof parsedOrgs === 'string' && parsedOrgs.startsWith('[')) parsedOrgs = JSON.parse(parsedOrgs);
                   
                   if (Array.isArray(parsedOrgs)) isValidForOrg = parsedOrgs.includes(orgId);
                   else if (typeof parsedOrgs === 'string') isValidForOrg = parsedOrgs.includes(orgId);
               } catch (e) {
                   isValidForOrg = String(offer.organization_id).includes(orgId);
               }
            }

            if (isValidForOrg) {
                // Parse hidden status safely
                let hiddenOrgs = [];
                try {
                  if (typeof offer.hidden_from_orgs === 'string') hiddenOrgs = JSON.parse(offer.hidden_from_orgs);
                  else if (Array.isArray(offer.hidden_from_orgs)) hiddenOrgs = offer.hidden_from_orgs;
                } catch(e) {}
                
                const isHiddenForCurrentOrg = hiddenOrgs.includes(orgId);

                // 👉 If Trainee view AND it's hidden, skip it entirely!
                if (!this.isActuallyAdminView && isHiddenForCurrentOrg) {
                    return; 
                }

                offersList.push({
                  ...offer,
                  isHiddenForCurrentOrg, // Used by UI to grey out the card for Admins
                  consultant_full_data: consultant
                });
            }
          });
        }
      });
      return offersList;
    },

    filteredOffers() {
      let result = this.allOffers;
      
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase().trim();
        result = result.filter(o => {
          const titleMatch = o.title && o.title.toLowerCase().includes(query);
          const nameMatch = o.consultant_full_data && o.consultant_full_data.name && o.consultant_full_data.name.toLowerCase().includes(query);
          const promiseMatch = o.valuePromise && o.valuePromise.toLowerCase().includes(query);
          const fitMatch = o.bestFitFor && o.bestFitFor.toLowerCase().includes(query);
          return titleMatch || nameMatch || promiseMatch || fitMatch;
        });
      }
      
      if (this.sortBy === 'rate') {
          result = [...result].sort((a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0));
      } else if (this.sortBy === 'rating') {
          result = [...result].sort((a, b) => parseFloat(b.consultant_full_data.rating_score || 0) - parseFloat(a.consultant_full_data.rating_score || 0));
      }
      
      return result;
    }
  },
  methods: {
    getProfileImageUrl(path) {
      if (!path) return '';
      if (path.startsWith('http')) return path;
      return `https://gtbrdd-storage.s3.us-west-2.amazonaws.com/editor/profile-pictures/${path}`;
    },
    viewProfile(offer) {
      this.selectedOffer = offer;
      this.selectedConsultant = offer.consultant_full_data;
      this.showProfileModal = true;
    },
    resetFilters() {
      this.searchQuery = "";
      this.selectedCategory = "All Categories";
      this.filters.availableThisWeek = false;
      this.filters.availableNextWeek = false;
    },

    async fetchAllDataInOneGo() {
      try {
        this.isLoadingData = true;
        let userEmail = null;
        
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        userEmail = userInfo.email || (this.$store && this.$store.state.AppActiveUser && this.$store.state.AppActiveUser.email) || (window.$cookies && window.$cookies.get('email'));

        if (!userEmail) return;

        const encodedEmail = encodeURIComponent(userEmail);
        const orgsResponse = await axios.get(`https://parser.ai.getboarded.com/api/v2/user-frameworks/by-email/${encodedEmail}`);
        
        if (orgsResponse.data && orgsResponse.data.status === 'success') {
          this.userOrgsList = orgsResponse.data.data;
          let tempMap = {}; 

          const fetchPromises = this.userOrgsList.map(async (org) => {
            try {
              const res = await axios.get(`https://parser.ai.getboarded.com/api/consultants?org_id=${org.org_id}`);
              if (res.data && res.data.status === 'success' && Array.isArray(res.data.data)) {
                tempMap[org.org_id] = res.data.data;
              } else {
                tempMap[org.org_id] = [];
              }
            } catch(e) {
              tempMap[org.org_id] = [];
            }
          });

          await Promise.all(fetchPromises);
          
          this.consultantsByOrg = { ...tempMap };
          console.log("✅ Background Data Loading Complete");
        }
      } catch (error) {
        console.error("Error fetching all data:", error);
      } finally {
        this.isLoadingData = false;
      }
    },

    // 🚀 NEW UPDATED TOGGLE VISIBILITY (Instant Re-render without opening card)
    async toggleVisibility(offer) {
      try {
        const orgId = this.currentMongoOrgId;
        if (!orgId) {
          this.$vs.notify({ title: 'Error', text: 'Organization ID not found.', color: 'danger' });
          return;
        }
        
        const newStatus = !offer.isHiddenForCurrentOrg;
        
        // 1. Optimistic UI update for the immediate cloned object
        offer.isHiddenForCurrentOrg = newStatus;

        // 2. Find and update the ORIGINAL source data so the computed property knows it changed
        let originalOfferRef = null;
        let originalHiddenOrgsBackup = null;

        if (this.consultantsByOrg[orgId]) {
          for (let consultant of this.consultantsByOrg[orgId]) {
            if (consultant.offers) {
              const matchedOfferIndex = consultant.offers.findIndex(o => o._id === offer._id);
              
              if (matchedOfferIndex !== -1) {
                let matchedOffer = consultant.offers[matchedOfferIndex];
                originalOfferRef = matchedOffer;
                
                // Parse current state securely
                let hiddenArr = [];
                try {
                  if (typeof matchedOffer.hidden_from_orgs === 'string') hiddenArr = JSON.parse(matchedOffer.hidden_from_orgs);
                  else if (Array.isArray(matchedOffer.hidden_from_orgs)) hiddenArr = [...matchedOffer.hidden_from_orgs];
                } catch(e) {}
                
                originalHiddenOrgsBackup = [...hiddenArr];
                
                // Modify the array based on the new status
                if (newStatus && !hiddenArr.includes(orgId)) {
                  hiddenArr.push(orgId);
                } else if (!newStatus) {
                  hiddenArr = hiddenArr.filter(id => id !== orgId);
                }
                
                // Update the deep property
                matchedOffer.hidden_from_orgs = hiddenArr;
                
                // 🔥 THE MAGIC FIX: Force Vue to recognize the change! 🔥
                // By reassigning the top-level object, Vue instantly re-runs the `allOffers` computed property.
                this.consultantsByOrg = { ...this.consultantsByOrg };
                break;
              }
            }
          }
        }

        // Call the backend endpoint
        const response = await axios.patch(`https://parser.ai.getboarded.com/api/offers/${offer._id}/visibility`, {
            org_id: orgId,
            is_hidden: newStatus
        });
        
        if (response.data.status === 'success') {
            this.$vs.notify({ 
                title: 'Visibility Updated', 
                text: `Offer is now ${newStatus ? 'hidden from' : 'visible to'} your users.`, 
                color: newStatus ? 'warning' : 'success',
                iconPack: 'feather',
                icon: newStatus ? 'icon-eye-off' : 'icon-eye'
            });
        } else {
            throw new Error("Backend returned failure status.");
        }
      } catch (error) {
        // REVERT UI ON FAILURE
        offer.isHiddenForCurrentOrg = !offer.isHiddenForCurrentOrg;
        if (originalOfferRef && originalHiddenOrgsBackup) {
            originalOfferRef.hidden_from_orgs = originalHiddenOrgsBackup;
            this.consultantsByOrg = { ...this.consultantsByOrg }; // Force revert UI update
        }
        
        console.error("Visibility toggle error:", error);
        this.$vs.notify({ title: 'Error', text: 'Failed to update visibility.', color: 'danger' });
      }
    },

    async askBeforeBook(offer) {
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        let userId = userInfo.usersId || userInfo.id || userInfo._id || userInfo.email || "unknown_user";

        if (!this.currentMongoOrgId) {
             this.$vs.notify({ title: 'Error', text: 'Organization not identified. Please switch orgs or refresh.', color: 'danger' });
             return;
        }

        const payload = {
            offer_id: offer._id,
            offer_title: offer.title, 
            consultant_id: offer.consultant_full_data.id,
            org_id: this.currentMongoOrgId,
            requester_id: userId
        };

        const response = await axios.post("https://parser.ai.getboarded.com/api/offer-requests", payload);

        if (response.data.status === 'success' || response.data.status === 'exists') {
            this.$vs.notify({
                title: response.data.status === 'exists' ? 'Already Requested' : 'Request sent',
                text: response.data.message,
                color: response.data.status === 'exists' ? 'warning' : 'success',
                iconPack: 'feather',
                icon: 'icon-check'
            });
            if (response.data.status === 'success') {
                this.showProfileModal = false;
            }
        }
      } catch (error) {
        console.error("Error requesting offer:", error);
        this.$vs.notify({ title: 'Error', text: 'Failed to send request.', color: 'danger' });
      }
    }
  },
  created() {
    this.fetchAllDataInOneGo();
  }
};
</script>

<style lang="scss" scoped>
/* ========================================================
   GLOBAL & PAGE STYLES
   ======================================================== */
#page-consultant-list {
  --ink: #222325; --ink-secondary: #74767e; --ink-muted: #b5b6ba;
  --border-c: #e5e7eb; --surface: #ffffff; --page-bg: #f9fafb;
  --primary: #1dbf73; --primary-hover: #19a463; 
  background: var(--page-bg); padding: 8px 16px; color: var(--ink);
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
}
.page-header { margin-bottom: 24px; }
.page-title { font-size: 28px; font-weight: 700; color: #404145; margin: 0 0 8px; }
.page-subtitle { color: var(--ink-secondary); font-size: 15px; margin: 0; line-height: 1.5; }
.toolbar { display: flex; align-items: center; gap: 16px; margin-bottom: 24px; flex-wrap: wrap; }
.search-container { display: flex; align-items: center; flex: 1; min-width: 260px; max-width: 400px; background: var(--surface); border: 1px solid var(--border-c); border-radius: 4px; padding: 0 14px; height: 40px; box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
.search-container:focus-within { border-color: var(--ink); }
.search-input { border: none; outline: none; background: transparent; width: 100%; padding: 0 10px; font-size: 14px; color: var(--ink); }
.filter-btn { display: flex; align-items: center; gap: 8px; height: 40px; padding: 0 16px; background: var(--surface); border: 1px solid var(--border-c); border-radius: 4px; color: var(--ink); font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.filter-btn:hover { background: #f5f5f5; }
.sort-control { margin-left: auto; }
.sort-select { width: 180px; }

/* ========================================================
   NEW EMPTY STATE STYLES 
   ======================================================== */
.empty-state-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 480px;
  overflow: hidden;
  margin-bottom: 24px;
}

.empty-state-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-icon-wrap {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 2px solid #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  background: #ffffff;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.03);
}

.empty-icon-wrap svg {
  color: #9ca3af;
}

.empty-title {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 12px 0;
}

.empty-text {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
  max-width: 480px;
  margin: 0 auto;
}

.btn-clear-search {
  margin-top: 24px;
  background: #111827;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-clear-search:hover {
  background: #374151;
  transform: translateY(-1px);
}

.empty-state-footer {
  display: flex;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.footer-link-card {
  flex: 1;
  padding: 24px 32px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: background 0.2s;
}

.footer-link-card:first-child {
  border-right: 1px solid #e5e7eb;
}

.footer-link-card:hover {
  background: #f3f4f6;
}

.footer-icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.footer-icon-circle svg {
  color: #9ca3af;
}

.footer-link-text {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.footer-link-text strong {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
}

.footer-link-text p {
  font-size: 13px;
  margin: 0;
}

.text-warning-link { color: #d97706; font-weight: 600; }
.text-muted-link { color: #6b7280; font-weight: 500; }

@media (max-width: 640px) {
  .empty-state-footer {
    flex-direction: column;
  }
  .footer-link-card:first-child {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }
}

/* ========================================================
   Background Offers Grid 
   ======================================================== */
.consulting-offers-grid { 
  display: grid; 
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); 
  gap: 24px; 
  align-items: stretch;
}
.consulting-offer-card { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; display: flex; flex-direction: column; cursor: pointer; transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease; }
.consulting-offer-card:hover { transform: translateY(-2px); border-color: #d1d5db; box-shadow: 0 8px 22px rgba(0, 0, 0, 0.08); }
.offer-card-banner { width: 100%; height: 175px; overflow: hidden; background: #111827; }
.offer-card-banner-img { display: block; width: 100%; height: 100%; object-fit: cover; }
.offer-card-no-banner { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #9ca3af; }
.offer-card-consultant { display: flex; align-items: center; gap: 10px; padding: 14px 16px 4px; }
.offer-card-avatar { width: 34px; height: 34px; border-radius: 50%; overflow: hidden; flex-shrink: 0; background: #f3f4f6; border: 1px solid #e5e7eb; }
.offer-card-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.offer-card-avatar-default { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #9ca3af; }
.offer-card-consultant-info { min-width: 0; }
.offer-card-consultant-name { font-size: 13px; font-weight: 700; color: #111827; }
.offer-card-consultant-role { font-size: 11px; color: #6b7280; margin-top: 2px; }
.offer-card-content { padding: 8px 16px 16px; }
.offer-card-best-fit { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; margin-bottom: 4px; }
.offer-card-title { font-size: 18px; line-height: 1.3; font-weight: 700; color: #047857; margin: 0 0 7px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.offer-card-promise-card { font-size: 12px; line-height: 1.45; color: #4b5563; margin: 0 0 12px; }
.offer-card-price-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; border-bottom: 1px solid #f3f4f6; padding-bottom: 11px; margin-bottom: 12px; }
.offer-card-price { font-size: 19px; line-height: 1; font-weight: 800; color: #111827; }
.offer-card-meta { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
.offer-card-format, .offer-card-delivery { display: inline-flex; align-items: center; gap: 4px; font-size: 10px; font-weight: 600; color: #4f46e5; }
.offer-card-section { margin-bottom: 12px; }
.offer-card-section-label { font-size: 10px; font-weight: 700; color: #6b7280; margin-bottom: 4px; }
.offer-card-section-text { font-size: 12px; line-height: 1.45; color: #4b5563; }
.offer-card-deliverables { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 5px; }
.offer-card-deliverables li { display: flex; align-items: flex-start; gap: 6px; font-size: 11px; line-height: 1.4; color: #4b5563; }
.offer-card-deliverables li svg { flex-shrink: 0; margin-top: 1px; color: #10b981; }
.offer-card-details-card { border-top: 1px solid #f3f4f6; padding-top: 10px; display: flex; flex-direction: column; gap: 6px; }
.offer-card-detail { display: flex; align-items: center; gap: 7px; font-size: 11px; color: #4b5563; }
.offer-card-detail svg { color: #6b7280; flex-shrink: 0; }
.offer-card-footer { margin-top: auto; border-top: 1px solid #f3f4f6; padding: 11px 16px; display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.offer-card-badges { display: flex; flex-wrap: wrap; gap: 5px; min-width: 0; }
.offer-badge { font-size: 9px; font-weight: 700; padding: 3px 7px; border-radius: 10px; white-space: nowrap; }
.offer-badge-risk { background: #fef9c3; color: #a16207; }
.offer-badge-exchange { background: #f3f4f6; color: #4b5563; }
.offer-card-view-btn { flex-shrink: 0; display: inline-flex; align-items: center; gap: 4px; border: none; background: transparent; color: #4f46e5; font-size: 11px; font-weight: 700; cursor: pointer; padding: 0; }
.offer-card-view-btn:hover { color: #3730a3; }
@media (max-width: 900px) { .consulting-offers-grid { grid-template-columns: repeat(2, minmax(280px, 1fr)); } }
@media (max-width: 600px) { .consulting-offers-grid { grid-template-columns: 1fr; } .offer-card-banner { height: 180px; } .offer-card-footer { align-items: flex-start; flex-direction: column; } }

/* ========================================================
   MODAL & LINKEDIN HEADER
   ======================================================== */
.profile-modal-premium { z-index: 99999; }
.profile-modal-premium ::v-deep .vs-popup { 
  overflow-x: hidden !important;
  width: 96vw !important; max-width: 840px !important; 
  border-radius: 16px !important; padding: 0 !important; 
  display: flex !important; flex-direction: column !important; 
  max-height: 92vh !important; overflow: hidden !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
}
.profile-modal-premium ::v-deep .vs-popup--header { display: none !important; }
.profile-modal-premium ::v-deep .vs-popup--content { padding: 0 !important; margin: 0 !important; overflow: hidden !important; display: flex !important; flex-direction: column !important; flex: 1 !important; }
.profile-modal-premium ::v-deep .modal-content-wrapper { background-color: #f9fafb; color: #222325; position: relative; display: flex; flex-direction: column; flex: 1; min-height: 0; font-family: 'Inter', 'Helvetica Neue', Helvetica, sans-serif; overflow: hidden; }
.profile-modal-premium ::v-deep .modal-close-btn { position: absolute; top: 12px; right: 12px; z-index: 50; background: rgba(0,0,0,0.5); border: none; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; color: #fff; cursor: pointer; transition: background 0.2s; }
.profile-modal-premium ::v-deep .modal-close-btn:hover { background: rgba(0,0,0,0.8); }

/* Invisible Scrollbar */
.profile-modal-premium ::v-deep .modal-scroll-area::-webkit-scrollbar { display: none; width: 0; }
.profile-modal-premium ::v-deep .modal-scroll-area { scrollbar-width: none; -ms-overflow-style: none; flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden; }

/* Header */
.linkedin-header { 
  position: relative; 
  border-bottom: 1px solid #e5e7eb; 
  background: #fff;
  width: 100%; 
  box-sizing: border-box;
}

.linkedin-cover { 
  width: 100%; 
  height: 180px; 
  position: relative; 
  display: flex;
  align-items: flex-start;
  padding: 32px 50px 32px 32px; 
  box-sizing: border-box;
}

/* New Title Typography inside the Color Banner */
.cover-title-wrapper {
  max-width: 85%;
}

.cover-title {
  color: #ffffff;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.25;
  margin: 0 0 6px 0;
  text-shadow: 0 2px 4px rgba(0,0,0,0.15); 
}

.cover-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
  text-shadow: 0 1px 2px rgba(0,0,0,0.1);
}
.linkedin-cover-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.5)); }
.linkedin-profile-section { display: flex; justify-content: space-between; align-items: flex-end; padding: 0 32px; margin-top: -50px; position: relative; z-index: 10; }
.linkedin-avatar-box { width: 110px; height: 110px; border-radius: 50%; border: 4px solid #fff; background: #f3f4f6; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
.linkedin-avatar-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.linkedin-avatar-default { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; color: #9ca3af; }
.linkedin-action-bar { margin-bottom: 8px; }
.linkedin-btn-primary { background: #ffffff; color: #374151; border: 1px solid #d1d5db; border-radius: 24px; padding: 8px 20px; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
.linkedin-btn-primary:hover { background: #f9fafb; border-color: #9ca3af; }
.linkedin-text-info { padding: 16px 32px 32px; }
.linkedin-name { font-size: 26px; font-weight: 800; color: #111827; margin: 0 0 4px 0; letter-spacing: -0.02em; }
.linkedin-headline { font-size: 16px; color: #4b5563; margin: 0 0 12px 0; font-weight: 500; }
.linkedin-contact-links { display: flex; gap: 16px; flex-wrap: wrap; }
.lnk-link { display: inline-flex; align-items: center; gap: 6px; font-size: 14px; color: #4f46e5; font-weight: 600; text-decoration: none; }
.lnk-link:hover { text-decoration: underline; color: #3730a3; }


/* ========================================================
   ✨ PREMIUM B2B OFFER CARD DESIGN ✨
   ======================================================== */
.modal-body { padding: 32px; display: flex; flex-direction: column; gap: 32px; }
.section-title { font-size: 18px; font-weight: 800; color: #111827; margin: 0 0 20px 0; text-transform: uppercase; letter-spacing: 0.05em; color: #6b7280; }

.premium-offers-list {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.premium-offer-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
}

.lovable-offer-card { 
  height: 100%; 
  background: #ffffff; 
  border: 1px solid #e5e7eb; 
  border-radius: 12px; 
  overflow: hidden; 
  display: flex; 
  flex-direction: column; 
  cursor: pointer; 
  transition: all 0.2s ease; 
  box-shadow: 0 1px 3px rgba(0,0,0,0.05); 
}
.lovable-offer-card:hover { transform: translateY(-3px); box-shadow: 0 12px 24px -8px rgba(0,0,0,0.1); border-color: #d1d5db; }
.loc-header { padding: 20px 24px; color: #ffffff; }
.loc-header-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
.loc-avatar { width: 44px; height: 44px; border-radius: 50%; overflow: hidden; border: 2px solid rgba(255,255,255,0.3); background: rgba(255,255,255,0.1); }
.loc-avatar img { width: 100%; height: 100%; object-fit: cover; }
.loc-avatar-placeholder { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; font-weight: 700; font-size: 18px; color: #fff; }
.loc-badge { font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; background: rgba(255,255,255,0.2); padding: 4px 8px; border-radius: 4px; backdrop-filter: blur(4px); }
.loc-title { font-size: 20px; line-height: 1.3; font-weight: 800; color: #ffffff; margin: 0 0 4px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; text-shadow: 0 1px 2px rgba(0,0,0,0.1); }
.loc-consultant-name { font-size: 12px; opacity: 0.9; font-weight: 500; text-shadow: 0 1px 2px rgba(0,0,0,0.1); }
.loc-body { padding: 20px 24px; display: flex; flex-direction: column; flex: 1; }
.loc-promise { 
  font-size: 13px; 
  line-height: 1.5; 
  color: #4b5563; 
  margin: 0 0 16px; 
  display: -webkit-box;
  -webkit-line-clamp: 3; 
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.loc-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 24px; }
.loc-tag { display: inline-flex; align-items: center; font-size: 10px; font-weight: 600; background: #f3f4f6; color: #4b5563; padding: 4px 8px; border-radius: 4px; }
.loc-footer { margin-top: auto; display: flex; align-items: flex-end; justify-content: space-between; gap: 10px; }
.loc-price { display: flex; flex-direction: column; gap: 4px; }
.loc-price-val { font-size: 22px; font-weight: 800; color: #111827; line-height: 1; }
.loc-price-meta { display: inline-flex; align-items: center; font-size: 11px; color: #6b7280; }
.loc-slots { display: inline-flex; align-items: center; font-size: 11px; font-weight: 600; color: #6b7280; }

/* ========================================================
   MODAL DETAILED OFFER CARDS
   ======================================================== */
.detailed-offer-card { display: flex; flex-direction: column; gap: 16px; margin-bottom: 32px; }
.doc-main { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 32px; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
.doc-top-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 32px; }
.doc-promise { font-size: 18px; line-height: 1.4; color: #111827; font-weight: 600; margin: 0; max-width: 70%; }
.doc-price-box { text-align: right; }
.doc-price-val { font-size: 28px; font-weight: 800; color: #111827; line-height: 1; margin-bottom: 6px; }
.doc-price-meta { display: flex; align-items: center; justify-content: flex-end; font-size: 13px; color: #6b7280; }
.doc-divider { border: 0; border-top: 1px solid #e5e7eb; margin: 24px 0; }
.doc-label { display: block; font-size: 12px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
.doc-text { font-size: 15px; color: #374151; line-height: 1.6; margin: 0; }
.doc-text-bold { font-size: 15px; font-weight: 600; color: #111827; }
.doc-check-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
.doc-check-list li { display: flex; align-items: flex-start; font-size: 14px; color: #374151; }
.doc-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 32px; }
.doc-cta-btn { width: 100%; background: #f59e0b; color: #ffffff; border: none; padding: 16px; border-radius: 8px; font-size: 16px; font-weight: 700; display: flex; justify-content: center; align-items: center; cursor: pointer; transition: background 0.2s; box-shadow: 0 4px 6px -1px rgba(245,158,11,0.2); }
.doc-cta-btn:hover { background: #d97706; }
.doc-cta-hint { text-align: center; font-size: 12px; color: #9ca3af; margin: 12px 0 0 0; }
.doc-commit-box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 12px; padding: 24px 32px; }
.doc-commit-title { font-size: 16px; font-weight: 800; color: #111827; margin: 0 0 20px 0; }
.doc-commit-item { display: flex; align-items: flex-start; gap: 16px; margin-bottom: 20px; }
.doc-commit-item:last-of-type { margin-bottom: 0; }
.doc-commit-content strong { display: block; font-size: 14px; color: #111827; margin-bottom: 4px; }
.doc-commit-content p { font-size: 13px; color: #4b5563; line-height: 1.5; margin: 0; }
.doc-reply-time { font-size: 13px; color: #6b7280; margin: 24px 0 0 0; }
.text-warning { color: #f59e0b; }
.text-success { color: #10b981; }
.text-danger { color: #ef4444; }
.text-primary { color: #3b82f6; }

@media (max-width: 768px) {
  .doc-top-row { flex-direction: column; gap: 16px; }
  .doc-promise { max-width: 100%; }
  .doc-price-box { text-align: left; }
  .doc-price-meta { justify-content: flex-start; }
  .doc-grid-2 { grid-template-columns: 1fr; gap: 16px; }
  .doc-commit-box { padding: 20px; }
}

.profile-modal-premium ::v-deep .vs-popup {
  overflow: hidden !important; 
}

.profile-modal-premium ::v-deep .vs-popup--content,
.profile-modal-premium ::v-deep .vs-popup-text {
  padding: 0 !important;
  margin: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  overflow-x: hidden !important;
}

.profile-modal-premium ::v-deep .modal-content-wrapper,
.profile-modal-premium ::v-deep .modal-scroll-area {
  width: 100% !important;
  max-width: 100% !important;
  padding: 0 !important;
  margin: 0 !important;
  overflow-x: hidden !important;
}

/* Ensure the header and color banner stretch edge-to-edge */
.linkedin-header { 
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}

.linkedin-cover { 
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 !important;
}

/* ========================================================
   VISIBILITY TOGGLE STYLES
   ======================================================== */
.is-hidden-offer {
  opacity: 0.65;
  filter: grayscale(40%);
}
.visibility-toggle-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
  z-index: 10;
}
.visibility-toggle-btn:hover {
  background: rgba(255, 255, 255, 0.4);
  transform: scale(1.05);
  border-color: #fff;
}
</style>